import {
  Box,
  Button,
  CircularProgress,
  Link,
  Modal,
  Stack,
  Typography,
} from "@mui/material";
import { CheckCircle, Cancel } from "@mui/icons-material";
import {
  ModalActionButtonStyles,
  ModalBaseStyles,
  ModalHeader,
} from "../../Common/styles/modal";
import { AxiosResponse } from "axios";
import http from "../../../utils/http";
import { errorToastMessage, toastMessage } from "../../../utils/toast";
import { useNavigate, useParams } from "react-router-dom";
import { useCallback, useEffect, useState } from "react";
import { useAppDispatch } from "../../../Redux/hooks";
import { setStateToggle } from "../../../Redux/reducers/studySlice";

type Props = {
  showModal: boolean;
  closeModal: () => void;
};

type GoLiveCheck = {
  id: string;
  label: string;
  pass: boolean;
  detail: string;
};

const fixPathFor = (checkId: string, studyId?: string): string | null => {
  if (!studyId) return null;
  switch (checkId) {
    case "mainContact":
      return `/studies/${studyId}/study-settings`;
    case "activeScreener":
      return `/studies/${studyId}/study-designer?type=screening`;
    default:
      return null;
  }
};

const ConfirmStudyLiveModal = ({ showModal, closeModal }: Props) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [submitLoader, setSubmitLoader] = useState(false);
  const [checks, setChecks] = useState<GoLiveCheck[]>([]);
  const [allPassed, setAllPassed] = useState(false);
  const [checksLoading, setChecksLoading] = useState(false);
  const { id: studyId } = useParams();

  const fetchChecks = useCallback(async () => {
    if (!studyId) return;
    try {
      setChecksLoading(true);
      const res: AxiosResponse = await http.get(
        `/study/${studyId}/go-live-checks`
      );
      const data = res?.data?.data ?? { checks: [], allPassed: false };
      setChecks(data.checks ?? []);
      setAllPassed(!!data.allPassed);
    } catch (err) {
      setChecks([]);
      setAllPassed(false);
      errorToastMessage(err as Error);
    } finally {
      setChecksLoading(false);
    }
  }, [studyId]);

  useEffect(() => {
    if (showModal) {
      fetchChecks();
    }
  }, [showModal, fetchChecks]);

  const submitHandler = async () => {
    try {
      setSubmitLoader(true);
      let url = `/study/${studyId}/live`;
      let res: AxiosResponse;
      res = await http.post(url);
      toastMessage("success", res.data.message);
      closeModal();
      setSubmitLoader(false);
      dispatch(setStateToggle());
    } catch (err) {
      setSubmitLoader(false);
      errorToastMessage(err as Error);
    }
  };

  return (
    <Modal open={showModal} onClose={closeModal}>
      <Box sx={{ ...ModalBaseStyles, minHeight: "20vh" }}>
        <ModalHeader title={"Go Live"} onCloseClick={closeModal} />
        <Box>
          <Typography variant="h3" color="text.primary" mb={1}>
            Pre-live checks
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>
            All checks must pass before this study can go live.
          </Typography>

          {checksLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <CircularProgress size={22} />
            </Box>
          ) : (
            <Stack spacing={1} mb={2}>
              {checks.map((check) => {
                const fixPath = !check.pass
                  ? fixPathFor(check.id, studyId)
                  : null;
                return (
                  <Stack
                    key={check.id}
                    direction="row"
                    spacing={1.5}
                    alignItems="flex-start"
                  >
                    {check.pass ? (
                      <CheckCircle sx={{ color: "success.main", mt: "2px" }} />
                    ) : (
                      <Cancel sx={{ color: "error.main", mt: "2px" }} />
                    )}
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body1" color="text.primary">
                        {check.label}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {check.detail}
                      </Typography>
                    </Box>
                    {fixPath && (
                      <Link
                        component="button"
                        type="button"
                        onClick={() => {
                          closeModal();
                          navigate(fixPath);
                        }}
                        sx={{ fontSize: 14, whiteSpace: "nowrap" }}
                      >
                        Fix
                      </Link>
                    )}
                  </Stack>
                );
              })}
            </Stack>
          )}

          {!allPassed && (
            <Link
              component="button"
              type="button"
              onClick={fetchChecks}
              disabled={checksLoading}
              sx={{ fontSize: 14, mb: 2, display: "inline-block" }}
            >
              Re-run checks
            </Link>
          )}

          <Box
            sx={{
              mt: 1,
              p: 1.5,
              borderRadius: 1,
              bgcolor: "action.hover",
              borderLeft: "3px solid",
              borderColor: "warning.main",
            }}
          >
            <Typography
              variant="body2"
              fontWeight={600}
              color="text.primary"
              mb={0.5}
            >
              Note: Making study live will do the following:
            </Typography>
            <Box component="ul" sx={{ pl: 2.5, m: 0 }}>
              <Typography component="li" variant="caption" color="text.secondary">
                Visits, Forms and Repeating data becomes read-only
              </Typography>
              <Typography component="li" variant="caption" color="text.secondary">
                Current participants and queries will be cleared
              </Typography>
              <Typography component="li" variant="caption" color="text.secondary">
                Surveys, Survey Packages and reminders cannot be changed
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box sx={{ ...ModalActionButtonStyles, mt: 2 }}>
          {!submitLoader ? (
            <>
              <Button onClick={closeModal} variant="outlined">
                Cancel
              </Button>
              <Button
                onClick={submitHandler}
                variant="contained"
                disabled={checksLoading || !allPassed}
              >
                Confirm
              </Button>
            </>
          ) : (
            <CircularProgress size={25} />
          )}
        </Box>
      </Box>
    </Modal>
  );
};

export default ConfirmStudyLiveModal;
